import React from 'react';
import {Link} from 'react-router-dom';
import {Formik, Form, Field} from 'formik';
import {FormControl} from '../form-control';
import {Checkbox} from '../checkbox';
import {Button, ButtonVariant, ButtonSize, ButtonType} from '../button';
import {Container, Grid, ContainerVariant, ContainerSize, ContainerPadding, GridCols, GridGap} from '../layout';
import {Heading, Text, HeadingVariant, TextVariant, TextColor, MarginBottom} from '../typography';
import {SignupFormData} from '../../types/signup';
import {Icons} from '../../assets/icons';
import {signupValidationSchema} from '../../utils/signup-validation-schema';
import {generateSubdomain} from '../../utils/subdomain-utils';
import {Input} from '../input';
import {ROUTES} from '../../constants/routes';

interface SignupFormProps {
  onSubmit: (values: SignupFormData, actions: any) => Promise<void>;
  className?: string;
}

const initialValues: SignupFormData = {
  firstName: '',
  lastName: '',
  email: '',
  businessName: '',
  portalUrl: '',
  agreeToTerms: false,
};

export const SignupForm: React.FC<SignupFormProps> = ({onSubmit, className = ''}) => {
  return (
    <Container variant={ContainerVariant.Form} padding={ContainerPadding.MD} className={className}>
      <Container variant={ContainerVariant.Centered} size={ContainerSize.MD}>
        <div className="mb-8">
          <Heading level={2} variant={HeadingVariant.PAGE_TITLE} marginBottom={MarginBottom.SM}>
            Create Your Account
          </Heading>
          <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
            Start finding exceptional talent today
          </Text>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={signupValidationSchema}
          onSubmit={(values, actions) => onSubmit(values, actions)}>
          {({values, errors, touched, isSubmitting, isValid, setFieldValue}) => {
            const isFormValid =
              isValid &&
              values.firstName &&
              values.lastName &&
              values.email &&
              values.businessName &&
              values.portalUrl &&
              values.agreeToTerms &&
              Object.keys(errors).length === 0;

            return (
              <Form className="space-y-5">
                <Grid cols={{sm: GridCols.ONE, md: GridCols.TWO}} gap={GridGap.MD}>
                  <FormControl label="First Name" error={touched.firstName ? errors.firstName : undefined} required>
                    <Field name="firstName">
                      {({field}: any) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your first name"
                          error={!!(touched.firstName && errors.firstName)}
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                  </FormControl>

                  <FormControl label="Last Name" error={touched.lastName ? errors.lastName : undefined} required>
                    <Field name="lastName">
                      {({field}: any) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your last name"
                          error={!!(touched.lastName && errors.lastName)}
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                  </FormControl>
                </Grid>

                <FormControl label="Email" error={touched.email ? errors.email : undefined} required>
                  <Field name="email">
                    {({field}: any) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="@sample.com"
                        error={!!(touched.email && errors.email)}
                        disabled={isSubmitting}
                        endEnhancer={<img src={Icons.MAIL_ICON} alt="Mail" className="w-5 h-5" />}
                      />
                    )}
                  </Field>
                </FormControl>

                <FormControl label="Business Name" error={touched.businessName ? errors.businessName : undefined} required>
                  <Field name="businessName">
                    {({field}: any) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="your company name"
                        error={!!(touched.businessName && errors.businessName)}
                        disabled={isSubmitting}
                        onChange={e => {
                          field.onChange(e);
                          const subdomain = generateSubdomain(e.target.value);
                          setFieldValue('portalUrl', subdomain);
                        }}
                        endEnhancer={<img src={Icons.BUILDING_ICON} alt="Building" className="w-5 h-5" />}
                      />
                    )}
                  </Field>
                </FormControl>

                <FormControl label="Portal URL" error={touched.portalUrl ? errors.portalUrl : undefined} required>
                  <Field name="portalUrl">
                    {({field}: any) => (
                      <div className="flex">
                        <div className="flex-1">
                          <input
                            {...field}
                            type="text"
                            placeholder="Enter a Valid Subdomain"
                            className={`w-full px-4 py-3 border border-r-0 rounded-l-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none ${
                              touched.portalUrl && errors.portalUrl
                                ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                            }`}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="relative">
                          <select
                            className="h-full pl-4 pr-12 py-3 border border-l-0 rounded-r-lg bg-gray-50 text-gray-700 cursor-pointer appearance-none min-w-[140px]"
                            disabled>
                            <option>.reccojoy.com</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <img src={Icons.LINK_ICON} alt="Link" className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                </FormControl>

                <Field name="agreeToTerms">
                  {() => (
                    <Checkbox
                      checked={values.agreeToTerms}
                      onChange={checked => setFieldValue('agreeToTerms', checked)}
                      error={touched.agreeToTerms ? errors.agreeToTerms : undefined}
                      disabled={isSubmitting}
                      required>
                      I agree to the{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-800 underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-800 underline">
                        Privacy Policy
                      </a>
                    </Checkbox>
                  )}
                </Field>

                <Button
                  type={ButtonType.SUBMIT}
                  variant={ButtonVariant.Primary}
                  size={ButtonSize.MD}
                  fullWidth
                  disabled={!isFormValid}
                  loading={isSubmitting}
                  loadingText="Creating Account...">
                  Continue
                </Button>

                <div className="text-center">
                  <Text variant={TextVariant.BODY_SMALL} color={TextColor.MUTED}>
                    Already have an account?{' '}
                    <Link to={ROUTES.LOGIN} className="text-blue-600 hover:text-blue-800 underline">
                      Login
                    </Link>
                  </Text>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </Container>
  );
};
